<template>
  <b-sidebar
    id="create-order-modal"
    title="Добавление новой заявки"
    header-class="bg-green"
    body-class="p-0"
    width="80em"
    backdrop
    right
    lazy
    no-close-on-backdrop
    z-index="1100"
  >
    <OrderCreateOrEdit
      v-if="contextLoaded"
      ref="orderForm"
      class="pt-3"
      :editedItem="compEditedItem"
      @change-order="changeOrder"
    >
      <template #footer>
        <b-overlay
          :show="saving"
          rounded
          opacity="0.8"
          spinner-small
          spinner-variant="success"
          class="ml-auto"
        >
          <b-button variant="success" @click="create">
            {{ isClient ? "Опубликовать заявку" : "Создать заявку" }}
          </b-button>
        </b-overlay>
      </template>
    </OrderCreateOrEdit>
    <div v-else class="p-4 text-center">
      <b-spinner variant="success"></b-spinner>
      <div class="mt-2">Загружаем данные компании...</div>
    </div>
  </b-sidebar>
</template>

<script>
import { API_ORDERS } from "@/constants/api";
import OrderCreateOrEdit from "@/components/orders/OrderCreateOrEdit";
import { mapActions, mapGetters } from "vuex";

// Local toast component to show a clickable link without using raw HTML strings
const ToastOrderCreated = {
  functional: true,
  props: {
    id: { type: [String, Number], required: true },
  },
  render(h, ctx) {
    const id = ctx.props.id;
    return h("span", [
      `Заказ №${id} создан. `,
      h(
        "a",
        { attrs: { href: `/orders?order=${id}` }, class: ["text-white"] },
        [h("u", "Открыть заказ »")]
      ),
    ]);
  },
};

export default {
  components: {
    OrderCreateOrEdit,
  },

  props: {
    reference_status_id: {
      type: Number,
      default: 359,
    },
    company: {
      type: Object,
      default: () => {},
    }
  },

  data() {
    return {
      order: {},
      saving: false,
      contextLoaded: true,
      previousStatus: null, // Для отслеживания изменений статуса
      editedItem: {
        id: "",
        text_mark: null,
        bitrix_deal_id: null,        
        creator_user_id: "",
        creator: {},
        company_employee_id: "",
        company_employee: {
          id: "",
          lead_fio: "",
          lead_email: "",
          lead_phone: "",
        },
        reference_order_type_id: 1,
        type: {},
        company_id: "",
        company: {},
        temp_name: "",
        contacts: {
          lead_fio: "",
          lead_email: "",
          lead_phone: "",
        },
        client: {},
        set_categories_ids: [], // Virtual for setting up categories
        categories: [],
        set_services_ids: [], // Virtual for setting up services
        services: [],
        reference_status_id: 359,
        status: {},
        receive_datetime: null,
        processing_end_datetime: null,
        comment: "",
        reference_close_reason_id: "",
        close_reason: {},
        close_comment: "",
        documents: [],
        short_order: false,
        optional_sales_manager_user_id: "",
        optional_sales_manager: {},
        outsourcing_order: 0,
        outsourcing_type_name: 'Нет',
        reference_sources_id: "",
        predictable_mp: null,
        related_orders: []
      }
    };
  },

  computed: {
    ...mapGetters("auth", ["currentUser", "isAuthenticated"]),

    isAgent() {
      return this.currentUser && this.currentUser.role && this.currentUser.role.includes("agent");
    },

    isClient() {
      return this.currentUser && this.currentUser.role && this.currentUser.role.includes("client");
    },

    compEditedItem() {
      if (this.company && this.company.id) {
        this.editedItem.company_id = this.company.id;
        this.editedItem.company = this.company;
      }
      
      return this.editedItem
    },
  },

  mounted() {
    // Инициализируем предыдущий статус
    this.previousStatus = this.reference_status_id;
    if (this.isClient) {
      this.loadClientContext();
    }
  },

  methods: {
    ...mapActions("orderDetail", ["orderWidgetSetChangedColumns"]),

    changeOrder(value) {
      this.order = value;
      
      // Отслеживаем изменения статуса заказа
      if (value.reference_status_id && value.reference_status_id !== this.previousStatus) {
        // Если статус изменился и это не первая установка
        if (this.previousStatus !== null) {
          // Отправляем сигнал обновления для старой и новой колонки
          const columnsToUpdate = [this.previousStatus, value.reference_status_id];
          this.orderWidgetSetChangedColumns(columnsToUpdate);
        }
        this.previousStatus = value.reference_status_id;
      }
    },

    loadClientContext() {
      this.contextLoaded = false;
      axios
        .get(`${API_ORDERS}/client-context`)
        .then(({ data }) => {
          this.editedItem.company_id = data.company.id;
          this.editedItem.company = data.company;
          this.editedItem.company_employee_id = data.company_employee.id;
          this.editedItem.company_employee = data.company_employee;
          this.editedItem.reference_status_id = 540;
          this.contextLoaded = true;
        })
        .catch(() => {
          this.$bvToast.toast("Не удалось получить связанную компанию. Обратитесь к менеджеру.", {
            title: "Заявка недоступна",
            variant: "danger",
            solid: true,
            noAutoHide: true
          });
        });
    },

    create() {
      // Валидация для агентов
      if (this.isAgent) {
        // Проверка наличия компании
        if (!this.order.company_id || this.order.company_id === "") {
          this.$bvToast.toast("Необходимо выбрать компанию для создания заявки", {
            title: "Ошибка валидации",
            variant: "danger",
            solid: true,
            autoHideDelay: 5000
          });
          return;
        }

        // Проверка наличия контакта в company_employee
        const companyEmployee = this.order.company_employee || {};
        const hasContact = (
          (companyEmployee.lead_fio && companyEmployee.lead_fio.trim()) ||
          (companyEmployee.lead_email && companyEmployee.lead_email.trim()) ||
          (companyEmployee.lead_phone && companyEmployee.lead_phone.trim())
        );

        if (!hasContact) {
          this.$bvToast.toast("Необходимо указать контактное лицо (ФИО, E-mail или Телефон) для создания заявки", {
            title: "Ошибка валидации",
            variant: "danger",
            solid: true,
            autoHideDelay: 5000
          });
          return;
        }
      }

      if (this.isClient) {
        if (!this.order.set_services_ids || this.order.set_services_ids.length === 0) {
          this.$bvToast.toast("Выберите хотя бы одну услугу", {
            title: "Заполните заявку",
            variant: "danger",
            solid: true
          });
          return;
        }
        if (!this.order.comment || !this.order.comment.trim()) {
          this.$bvToast.toast("Опишите задачу и ожидаемый результат", {
            title: "Заполните заявку",
            variant: "danger",
            solid: true
          });
          return;
        }
      }

      this.saving = true;
      axios
        .post(
          API_ORDERS,
          Object.assign(this.order, {
            reference_status_id: this.isClient ? 540 : this.reference_status_id,
          })
        )
        .then(({ data }) => {
          const finishCreate = () => {
            this.$toast(
              { component: ToastOrderCreated, props: { id: data.id } },
              { type: "success" }
            );
            this.$root.$emit("bv::toggle::collapse", "create-order-modal");
            this.$emit("created-order", data);

            // Отправляем сигнал обновления колонки канбан доски
            this.orderWidgetSetChangedColumns([this.reference_status_id]);
          };

          if (this.isClient && this.$refs.orderForm && this.$refs.orderForm.uploadPendingDocuments) {
            return this.$refs.orderForm
              .uploadPendingDocuments(data.id, data.company_id)
              .then(finishCreate)
              .catch(finishCreate);
          }

          finishCreate();
        })
        .catch((error) => {
          const message = error.response && error.response.data && error.response.data.error
            ? "Проверьте заполнение заявки"
            : "Не удалось создать заявку";
          this.$bvToast.toast(message, {
            title: "Ошибка",
            variant: "danger",
            solid: true
          });
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style>
</style>
